/**
 * QR token formats.
 *
 * The database mints these (`private.sign_token`) and verifies them
 * (`private.verify_token`). The apps only need to recognise what a scanner
 * returned, so the signing secret never reaches a browser.
 *
 * Wire format: `<prefix>.<base64url payload>.<base64url hmac>`
 */

/** Printed and stuck to a table. Payload is a `restaurant_tables.id`. */
export const TABLE_TOKEN_PREFIX = 'YMT1';

/** Rendered on the customer's phone for collection. Payload is an `orders.id`. */
export const PICKUP_TOKEN_PREFIX = 'YMP1';

/**
 * The prefix of a token, when it looks like one of ours.
 *
 * Mirrors the server check rather than re-implementing the signature, so the
 * merchant scanner can say "that is a Yummy pickup code" before spending a
 * round trip on verification.
 *
 * @param token Raw scanned text.
 * @returns The prefix, or null when the text is not a Yummy token.
 */
export function tokenPrefix(token: string | null | undefined): string | null {
  if (!token) return null;
  const prefix = token.split('.')[0];
  if (prefix === TABLE_TOKEN_PREFIX || prefix === PICKUP_TOKEN_PREFIX) return prefix;
  return null;
}

/**
 * Whether scanned text is a token this system issued.
 *
 * @param token Raw scanned text.
 * @returns True for a well-formed Yummy token.
 */
export function isYummyToken(token: string | null | undefined): boolean {
  if (!token) return false;
  const parts = token.split('.');
  return parts.length === 3 && tokenPrefix(token) !== null;
}

/**
 * Whether scanned text should be treated as a collectable pickup code.
 *
 * @param token Raw scanned text.
 * @returns True for a `YMP1` token.
 */
export function isPickupToken(token: string | null | undefined): boolean {
  return tokenPrefix(token) === PICKUP_TOKEN_PREFIX;
}

/**
 * Extracts the scanned value from a full URL, when a QR encodes one.
 *
 * Merchant scanners frequently pick up a URL printed inside a QR rather than a
 * bare token, so accepting both avoids a confusing failure at the counter.
 *
 * @param scanned Raw scanned text, possibly a URL.
 * @returns The embedded token or slug, or the input trimmed.
 */
export function normalizeScan(scanned: string): string {
  const trimmed = scanned.trim();

  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const url = new URL(trimmed);
      const segments = url.pathname.split('/').filter(Boolean);
      // /t/<token>, /r/<slug> and /pickup/<token> are the shapes the apps emit.
      // The last is what the customer's collection code encodes — a merchant's
      // in-app scanner reads that whole URL, so without this the token never
      // reaches the lookup and every genuine pickup code fails as a stranger.
      if (
        segments.length === 2 &&
        (segments[0] === 't' || segments[0] === 'r' || segments[0] === 'pickup')
      ) {
        return decodeURIComponent(segments[1]!);
      }
    } catch {
      return trimmed;
    }
  }

  return trimmed;
}

/**
 * What a scanned string names, when it names something the till can look up.
 *
 * Split from the lookup so the decision — is this a pickup token or an order
 * code, and is it anything at all — is one testable function rather than a
 * branch buried in a click handler.
 */
export type ScanTarget =
  | { kind: 'pickup'; token: string }
  | { kind: 'code'; code: string };

/**
 * Classifies scanned text as the thing it names.
 *
 * A pickup token is taken as itself. Anything else short and alphanumeric is
 * treated as the order code a customer reads out, which is the same field the
 * counter types into; everything else — a table sticker, a restaurant link, a
 * QR that belongs to somebody else entirely — is refused rather than sent to the
 * server on the off chance.
 *
 * @param scanned Raw scanned text, possibly a URL.
 * @returns What it names, or null when it is neither.
 */
export function scanTarget(scanned: string): ScanTarget | null {
  const value = normalizeScan(scanned);
  if (isPickupToken(value)) return { kind: 'pickup', token: value };
  if (/^[A-Za-z0-9]{4,8}$/.test(value)) return { kind: 'code', code: value.toUpperCase() };
  return null;
}
